import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CustomSelect, HeaderInput, HeaderTemplate, TitleHeader } from 'web-library';
import {
  ExamTypeModel,
  useGetExamType,
} from '../../../api-refactored/exam-configuration/useGetExamType';
import { useGetFeatureType } from '../../../api-refactored/exam-configuration/useGetFeatureType';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import ExamConfigurationContent from './YearOfAdmissionAndExamVacancy/ExamConfigurationContent';
import CandidateHistory from './CandidateHistory';
import ExamFee from './ExamFee';
import NoAccessContent from './NoAccessContent';
import LoadingComponent from '../../../components/LoadingComponent';
import ValidCeqTestDatePage from './ValidCeqTestDate';

const useHeadersDropdown = (selectedExamType: string) => {
  const examType = useGetExamType({});
  const { functionCode: examTypeFunctionCode, typeMapping: examTypeMapping } = useAPIDropdown(
    () => examType,
    false,
    dropDownMappingToSelect
  );
  const featureType = useGetFeatureType({
    uuid: selectedExamType || '',
  });

  const { functionCode: featureTypeFunctionCode, typeMapping: featureTypeMapping } = useAPIDropdown(
    () => featureType,
    false,
    dropDownMappingToSelect
  );
  return {
    examTypeFunctionCode,
    examTypeMapping,
    featureTypeFunctionCode,
    featureTypeMapping,
    examType,
  };
};

const checkIsAeisExam = (examType: ExamTypeModel[], targetUuid: string): boolean =>
    examType.find((e) => e.code === targetUuid)?.aeisExam ?? false;

const checkIsStrictlyAeisExam = (examType: ExamTypeModel[], targetUuid: string): boolean =>
    examType.find((e) => e.code === targetUuid)?.description === 'AEIS';

const ExamConfiguration = () => {
  const { t } = useTranslation();

  const [selectedExamType, setSelectedExamType] = useState<string>();
  const [selectedExamTypeDisplay, setSelectedExamTypeDisplay] = useState<string>();
  const [selectedFeatureTypeDisplay, setSelectedFeatureTypeDisplay] = useState<string>();
  const [isAeisExam, setIsAeisExam] = useState<boolean>(false);
  const [isStrictlyAeisExam, setIsStrictlyAeisExam] = useState<boolean>(false);
  const [noAccess, setNoAccess] = useState<boolean>(false);

  const {
    examTypeFunctionCode,
    examTypeMapping,
    featureTypeFunctionCode,
    featureTypeMapping,
    examType,
  } = useHeadersDropdown(selectedExamType || '');

  const { data: examTypeData } = examType;

  return (
      <>
        {examTypeData ? (
            <>
              <TitleHeader title={t('exam->>ExamConfiguration.NonNationalExamConfiguration')}/>
              <HeaderTemplate>
                <HeaderInput
                    label={t('exam->>ExamConfiguration.ExamType')}
                    input={
                      <CustomSelect
                          id="inputSelect-examType"
                          isHeader
                          displayEmpty
                          onChange={(event) => {
                            setSelectedExamType(event.target.value as string);
                            setSelectedExamTypeDisplay(
                                examTypeMapping[event.target.value as string].children
                            );
                            setSelectedFeatureTypeDisplay('');
                            setIsAeisExam(checkIsAeisExam(examTypeData, event.target.value as string));
                            setIsStrictlyAeisExam(
                                checkIsStrictlyAeisExam(examTypeData, event.target.value as string)
                            );
                            setNoAccess(false);
                          }}
                          menuItems={examTypeFunctionCode}
                          variant="filled"
                          placeholder={t('exam->>ExamConfiguration.SelectExamType')}
                      />
                    }
                />
              </HeaderTemplate>
              <HeaderTemplate>
                <HeaderInput
                    label={t('exam->>ExamConfiguration.FeatureType')}
                    input={
                      <CustomSelect
                          id="inputSelect-featureType"
                          key={`inputSelect-featureType-${selectedExamType}`}
                          isHeader
                          displayEmpty
                          disabled={!selectedExamType}
                          onChange={(event) => {
                            setSelectedFeatureTypeDisplay(
                                featureTypeMapping[event.target.value as string].children
                            );
                            setNoAccess(false);
                          }}
                          menuItems={featureTypeFunctionCode}
                          variant="filled"
                          placeholder={t('exam->>ExamConfiguration.SelectFeatureType')}
                      />
                    }
                />
              </HeaderTemplate>
              {noAccess && <NoAccessContent/>}
              {!noAccess &&
                  selectedExamType &&
                  selectedFeatureTypeDisplay === 'Year of Admission and Exam Vacancy' && (
                      <ExamConfigurationContent
                          selectedExamTypeUuid={selectedExamType}
                          selectedExamTypeDisplay={selectedExamTypeDisplay}
                          setNoAccess={setNoAccess}
                      />
                  )}
              {!noAccess &&
                  selectedExamType &&
                  selectedExamTypeDisplay &&
                  selectedFeatureTypeDisplay === 'Candidate History' && (
                      <Box
                          key={`candidate-history-${selectedExamTypeDisplay}-${selectedFeatureTypeDisplay}`}
                      >
                        <CandidateHistory
                            selectedExamTypeUuid={selectedExamType}
                            selectedExamTypeDisplay={selectedExamTypeDisplay}
                            selectedFeatureTypeDisplay={selectedFeatureTypeDisplay}
                            setNoAccess={setNoAccess}
                        />
                      </Box>
                  )}
              {!noAccess &&
                  selectedExamType &&
                  selectedFeatureTypeDisplay === t('exam->>ExamFeeConfiguration.ExamFees') && (
                      <ExamFee
                          selectedExamTypeUuid={selectedExamType}
                          selectedExamTypeDisplay={selectedExamTypeDisplay}
                          isAeisExam={isAeisExam}
                          setNoAccess={setNoAccess}
                      />
                  )}
              {!noAccess &&
                  selectedExamType &&
                  selectedFeatureTypeDisplay ===
                  t('exam->>ValidCeqTestDateConfiguration.ValidCeqTestDate') && (
                      <ValidCeqTestDatePage
                          selectedExamTypeUuid={selectedExamType}
                          selectedExamTypeDisplay={selectedExamTypeDisplay}
                          isStrictlyAeisExam={isStrictlyAeisExam}
                          setNoAccess={setNoAccess}
                      />
                  )}
            </>
        ) : (
            <LoadingComponent/>
        )}
      </>
  );
};

export default ExamConfiguration;
